import React, { useState } from "react";
import { addEvent, emptyEventsCache } from "../../../api/events";
import { searchCity } from "../../../api/citySearch";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useHistory } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { formatDateWithOptionalTime } from '../../../utils/dateUtils';
import DataTable from 'react-data-table-component';
import { ROUTES } from "../../../config/routes";
import { saveAs } from 'file-saver/dist/FileSaver.js';

const ImportEventsPage = () => {
    const history = useHistory();
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [, setSuccess] = useState(null);
    const [events, setEvents] = useState([]);
    const [, setIsValidated] = useState(false);
    const [errorRows, setErrorRows] = useState([]);
    const [errorCells, setErrorCells] = useState({});
    const [importSummary, setImportSummary] = useState({ total: 0, valid: 0, invalid: 0 });
    const [importProgress, setImportProgress] = useState(0);

    const onFileChange = (e) => {
        const selectedFile = e.target.files[0];
        const fileType = selectedFile.type;

        if (fileType !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" && fileType !== "application/vnd.ms-excel") {
            setError("Merci d'uploader un fichier Excel valide.");
            setFile(null);
        } else {
            setError(null);
            setFile(selectedFile);
        }
    };

    const convertExcelDateToJSDate = (excelDate) => {
        if (!excelDate) return null;
        // If it's already a Date object (from ExcelJS)
        if (excelDate instanceof Date) {
            return excelDate;
        }
        // If it's a string in French format (DD/MM/YYYY or DD/MM/YYYY HH:mm)
        if (typeof excelDate === 'string') {
            // Match DD/MM/YYYY or DD/MM/YYYY HH:mm
            const dateTimeMatch = excelDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
            if (dateTimeMatch) {
                const day = parseInt(dateTimeMatch[1], 10);
                const month = parseInt(dateTimeMatch[2], 10) - 1; // JS months are 0-based
                const year = parseInt(dateTimeMatch[3], 10);
                if (dateTimeMatch[4] && dateTimeMatch[5]) {
                    const hour = parseInt(dateTimeMatch[4], 10);
                    const minute = parseInt(dateTimeMatch[5], 10);
                    return new Date(year, month, day, hour, minute);
                } else {
                    return new Date(year, month, day);
                }
            }
            // Fallback: try Date.parse
            const parsed = new Date(excelDate);
            if (!isNaN(parsed.getTime())) return parsed;
            return null;
        }
        // For legacy support - if it's a numeric Excel date
        if (typeof excelDate === 'number') {
            const msDate = (excelDate - (25567 + 2)) * 86400 * 1000;
            const date = new Date(msDate);
            // Check if the original Excel date has no time component
            const isDateOnly = Math.floor(excelDate) === excelDate;
            if (isDateOnly) {
                // Set time to midnight for date-only values
                return new Date(date.getFullYear(), date.getMonth(), date.getDate());
            }
            return date; // Keep the time component if it exists
        }
        return null;
    };

    const onFileUpload = async () => {
        if (!file) {
            setError("Aucun fichier sélectionné ou type de fichier invalide.");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(await file.arrayBuffer());
            
            const worksheet = workbook.worksheets[0];
            if (!worksheet) {
                throw new Error("Le fichier Excel ne contient aucune feuille de calcul.");
            }
            
            // Get headers from the first row
            const headers = [];
            worksheet.getRow(1).eachCell((cell) => {
                headers.push(cell.value);
            });
            
            const requiredColumns = ["title", "description", "date", "location", "category", "speaker", "organizer", "endDate"];
            const hasAllColumns = requiredColumns.every(col => headers.includes(col));

            if (!hasAllColumns) {
                throw new Error("Le fichier Excel doit contenir toutes les colonnes suivantes : " + requiredColumns.join(", "));
            }
            
            // Create a mapping of column names to indices
            const columnMap = {};
            headers.forEach((header, index) => {
                columnMap[header] = index + 1; // ExcelJS uses 1-based indices
            });
            
            // Extract data
            const eventsData = [];
            worksheet.eachRow((row, rowNumber) => {
                // Skip the header row
                if (rowNumber === 1) return;
                
                const rowData = {};
                headers.forEach((header) => {
                    const cellValue = row.getCell(columnMap[header]).value;
                    rowData[header] = cellValue;
                });
                eventsData.push(rowData);
            });

            // Check data format
            const invalidRows = [];
            const invalidCells = {};
            const formattedEventsData = eventsData.map((event, index) => {
                const rowErrors = [];
                const rawDateValue = event.date;
                const rawEndDateValue = event.endDate;
                let dateObj = null;
                let endDateObj = null;

                try {
                    if (!rawDateValue) {
                        rowErrors.push("date");
                    } else {
                        dateObj = convertExcelDateToJSDate(rawDateValue);
                        if (isNaN(dateObj.getTime())) {
                            rowErrors.push("date");
                        }
                    }

                    if (rawEndDateValue) {
                        endDateObj = convertExcelDateToJSDate(rawEndDateValue);
                        if (isNaN(endDateObj.getTime())) {
                            rowErrors.push("endDate");
                        } else {
                            // Check if endDate is already past (compare to yesterday 23:59:59)
                            const now = new Date();
                            const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate()-1, 23, 59, 59);
                            if (endDateObj < todayEnd) {
                                rowErrors.push("endDate passée");
                            }
                        }
                    }
                } catch (error) {
                    if (!dateObj) rowErrors.push("date");
                    if (rawEndDateValue && !endDateObj) rowErrors.push("endDate");
                }

                if (!event.title) rowErrors.push("title");
                if (!event.description) rowErrors.push("description");
                if (!event.location || !event.location.toString().match(/(.*) \((\d+)\)/)) rowErrors.push("location");
                if (!event.category) rowErrors.push("category");

                if (rowErrors.length > 0) {
                    invalidRows.push(index);
                    invalidCells[index] = rowErrors;
                }

                return {
                    ...event,
                    id: `${index + 1}`, // Start id from 1
                    date: dateObj || rawDateValue, // Keep raw value if invalid
                    endDate: endDateObj || (rawEndDateValue || null), // Keep raw value if invalid
                    _rawDate: rawDateValue, // Store original value for display
                    _rawEndDate: rawEndDateValue, // Store original value for display
                    location: event.location ? event.location.toString() : '' // Convert to string if needed
                };
            });

            if (invalidRows.length > 0) {
                setErrorRows(invalidRows);
                setErrorCells(invalidCells);
            }

            // log formatted data
            console.log(formattedEventsData);

            setEvents(formattedEventsData);
            setIsValidated(false);

            // Display import summary
            const total = formattedEventsData.length;
            const invalid = invalidRows.length;
            const valid = total - invalid;
            setImportSummary({ total, valid, invalid });
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImportValidEvents = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setErrorRows([]);
        setErrorCells({});
        setImportProgress(0);

        // Only import valid events
        const validEvents = events.filter((_, idx) => !errorRows.includes(idx));
        const failedRows = [];
        const total = validEvents.length;

        try {
            for (const [index, event] of validEvents.entries()) {
                try {
                    const locationMatch = event.location.match(/(.*) \((\d+)\)/);
                    const cityName = locationMatch[1];
                    const postalCode = locationMatch[2];

                    const geoData = await searchCity(cityName, postalCode);
                    if (geoData.length === 0) {
                        throw new Error(`Echec pour retrouver la localisation géographique : ${event.location}`);
                    }

                    const eventData = {
                        title: event.title,
                        description: event.description,
                        speaker: event.speaker || null,
                        organizer: event.organizer || null,
                        date: event.date,
                        endDate: event.endDate || null,
                        location: {
                            cityName: cityName,
                            postalCode: postalCode,
                            geo: geoData[0].geo
                        },
                        category: event.category,
                        image: null
                    };

                    const result = await addEvent(eventData);
                    if (!result.success) {
                        throw new Error("Echec dans l'ajout d'évènement : " + event.title);
                    }
                } catch (rowError) {
                    // Mark this row as failed (should not happen for valid events, but just in case)
                    failedRows.push(events.findIndex(e => e.id === event.id));
                }
                setImportProgress(Math.round(((index + 1) / total) * 100));
            }

            if (failedRows.length === 0) {
                setSuccess("Evènements valides importés avec succès.");
                toast.success('Evènements valides importés avec succès.');
                emptyEventsCache();
                setTimeout(() => {
                    history.push(ROUTES.ALL_EVENTS.path);
                }, 2000);
            } else {
                setErrorRows(failedRows);
                setError("Certains évènements valides n'ont pas pu être importés.");
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
            setImportProgress(0);
        }
    };

    const handleExportInvalidEvents = async () => {
        if (!events.length || !errorRows.length) return;
        const ExcelJS = (await import('exceljs')).default || (await import('exceljs'));
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Invalid Events');
        // Exclude id, _rawDate, _rawEndDate from export
        const headers = Object.keys(events[0] || {}).filter(h => !['id', '_rawDate', '_rawEndDate'].includes(h));
        worksheet.columns = [
            ...headers.map(header => ({ header, key: header, width: 20 })),
            { header: 'errorDetails', key: 'errorDetails', width: 40 }
        ];
        errorRows.forEach(idx => {
            const event = events[idx];
            const row = headers.reduce((acc, key) => {
                acc[key] = event[key];
                return acc;
            }, {});
            row.errorDetails = (errorCells[idx] || []).join(', ');
            worksheet.addRow(row);
        });
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const now = new Date();
        const pad = n => n.toString().padStart(2, '0');
        const dateStr = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        saveAs(blob, `invalid_events_${dateStr}.xlsx`);
    };

    const handleReject = () => {
        setEvents([]);
        setIsValidated(false);
        setFile(null);
        document.getElementById('fileInput').value = null;
        setError(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onFileUpload();
    };

    const columns = [
        {
            name: 'ID',
            selector: row => row.id,
            sortable: false,
            width: '80px',
        },
        {
            name: '',
            cell: row => errorRows.includes(events.indexOf(row)) ? '❌' : '✅',
            width: '50px',
        },
        { name: 'Title', selector: row => row.title, sortable: true, cell: row => (
            <div
                style={{ backgroundColor: errorCells[events.indexOf(row)]?.includes("title") ? '#ffcccc' : 'transparent' }}
                title={errorCells[events.indexOf(row)]?.includes("title") ? errorCells[events.indexOf(row)].join(', ') : ''}
                onClick={e => {
                    if (window.innerWidth <= 800 && errorCells[events.indexOf(row)]?.includes("title")) {
                        alert(errorCells[events.indexOf(row)].join(', '));
                    }
                }}
            >
                {row.title}
            </div>
        ) },
        { name: 'Description', selector: row => row.description, sortable: true, grow: 2, cell: row => (
            <div
                style={{ backgroundColor: errorCells[events.indexOf(row)]?.includes("description") ? '#ffcccc' : 'transparent' }}
                title={errorCells[events.indexOf(row)]?.includes("description") ? errorCells[events.indexOf(row)].join(', ') : ''}
                onClick={e => {
                    if (window.innerWidth <= 800 && errorCells[events.indexOf(row)]?.includes("description")) {
                        alert(errorCells[events.indexOf(row)].join(', '));
                    }
                }}
            >
                {row.description}
            </div>
        ) },
        { 
            name: 'Date', 
            selector: row => row._rawDate, 
            sortable: true, 
            cell: row => (
                <div 
                    style={{ backgroundColor: errorCells[events.indexOf(row)]?.some(e => e.startsWith('date')) ? '#ffcccc' : 'transparent', padding: '4px', width: '100%' }}
                    title={errorCells[events.indexOf(row)]?.filter(e => e.startsWith('date')).join(', ') || ''}
                    onClick={e => {
                        if (window.innerWidth <= 800 && errorCells[events.indexOf(row)]?.some(e => e.startsWith('date'))) {
                            alert(errorCells[events.indexOf(row)].filter(e => e.startsWith('date')).join(', '));
                        }
                    }}
                >
                    {row.date instanceof Date && !isNaN(row.date.getTime()) 
                        ? formatDateWithOptionalTime(row.date)
                        : <span style={{ color: 'red' }}>{String(row._rawDate)}</span>
                    }
                </div>
            )
        },
        { 
            name: 'End Date', 
            selector: row => row._rawEndDate || '-', 
            sortable: true, 
            cell: row => (
                <div 
                    style={{ backgroundColor: errorCells[events.indexOf(row)]?.some(e => e.startsWith('endDate')) ? '#ffcccc' : 'transparent', padding: '4px', width: '100%' }}
                    title={errorCells[events.indexOf(row)]?.filter(e => e.startsWith('endDate')).join(', ') || ''}
                    onClick={e => {
                        if (window.innerWidth <= 800 && errorCells[events.indexOf(row)]?.some(e => e.startsWith('endDate'))) {
                            alert(errorCells[events.indexOf(row)].filter(e => e.startsWith('endDate')).join(', '));
                        }
                    }}
                >
                    {!row._rawEndDate ? '-' : (
                        row.endDate instanceof Date && !isNaN(row.endDate.getTime())
                            ? formatDateWithOptionalTime(row.endDate)
                            : <span style={{ color: 'red' }}>{String(row._rawEndDate)}</span>
                    )}
                </div>
            )
        },
        { name: 'Location', selector: row => row.location, sortable: true, cell: row => (
            <div
                style={{ backgroundColor: errorCells[events.indexOf(row)]?.includes("location") ? '#ffcccc' : 'transparent' }}
                title={errorCells[events.indexOf(row)]?.includes("location") ? errorCells[events.indexOf(row)].join(', ') : ''}
                onClick={e => {
                    if (window.innerWidth <= 800 && errorCells[events.indexOf(row)]?.includes("location")) {
                        alert(errorCells[events.indexOf(row)].join(', '));
                    }
                }}
            >
                {row.location}
            </div>
        ) },
        { name: 'Category', selector: row => row.category, sortable: true, cell: row => (
            <div
                style={{ backgroundColor: errorCells[events.indexOf(row)]?.includes("category") ? '#ffcccc' : 'transparent' }}
                title={errorCells[events.indexOf(row)]?.includes("category") ? errorCells[events.indexOf(row)].join(', ') : ''}
                onClick={e => {
                    if (window.innerWidth <= 800 && errorCells[events.indexOf(row)]?.includes("category")) {
                        alert(errorCells[events.indexOf(row)].join(', '));
                    }
                }}
            >
                {row.category}
            </div>
        ) },
    ];

    const customStyles = {
        header: {
            style: {
                minHeight: '56px',
                backgroundColor: '#e3f2fd',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#e3f2fd',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:nth-of-type(odd)': {
                    backgroundColor: '#f1f8ff',
                },
            },
        },
    };

    const conditionalRowStyles = [
        {
            when: row => errorRows.includes(events.indexOf(row)),
            style: {
                backgroundColor: '#ffcccc',
            },
        },
    ];

    return (
        <div className="contact-wrapper overflow-hidden">
            <ToastContainer />
            <div className="container pt-120 position-relative">
                <div className="row">
                    <div className="col-lg-12">
                        <h2>Import Events</h2>
                        
                        <form onSubmit={handleSubmit}>
                            <input id="fileInput" type="file" onChange={onFileChange} />
                            
                            <div className="submit-btn mt-2" >
                                <button type="submit"
                                    disabled={loading || !file}
                                    className="btn btn-primary">
                                    {loading ? "En cours d'import..." : 'Import évènements'}
                                </button>
                            </div>
                        </form>
                        {error && <p className="text-danger">{error}</p>}
                        {loading && importProgress > 0 && (
                            <div className="d-flex align-items-center mt-3">
                                <div className="spinner-border text-primary me-2" role="status">
                                    <span className="visually-hidden">Import en cours...</span>
                                </div>
                                <span>Importation des évènements : {importProgress}%</span>
                            </div>
                        )}
                        {events.length > 0 && (
                            <>
                                <div className="mt-3 d-flex align-items-center">
                                    <button className="btn btn-success me-2" onClick={handleImportValidEvents} disabled={loading}>
                                        Importer les évènements valides
                                    </button>
                                    <button className="btn btn-danger me-2" onClick={handleReject} disabled={loading}>
                                        Rejeter l'import
                                    </button>
                                    <button className="btn btn-warning me-2" onClick={handleExportInvalidEvents} disabled={loading || !errorRows.length}>
                                        Exporter les évènements invalides
                                    </button>
                                    <div className="ms-auto" style={{ fontWeight: 'bold' }}>
                                        Résumé import événements : {importSummary.total} importés, {importSummary.valid} valides, {importSummary.invalid} invalides
                                    </div>
                                </div>
                            
                                <div className="mt-3">
                                    <h3>Prévisualisation des évènements importés</h3>
                                    <DataTable
                                        columns={columns}
                                        data={events}
                                        keyField="id"
                                        pagination
                                        striped
                                        highlightOnHover
                                        responsive
                                        customStyles={customStyles}
                                        conditionalRowStyles={conditionalRowStyles}
                                        dense
                                    />
                                
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImportEventsPage;